// Low-level EventSource wrapper for the bot /events SSE stream.
// The React hook layer (useEventStream) turns emitted messages into React
// Query cache invalidations; this module only handles the transport:
// - auto-reconnect with backoff on drop
// - single shared connection per tab (module-scoped state)
// - typed subscribe() API over named events
//
// Auth: EventSource always sends same-origin cookies; when pointed at the
// cross-origin API host we must set `withCredentials: true` so the session
// cookie rides along (identical to fetch({ credentials: "include" })).

import { API_BASE } from "./api-client";

export type EventTopic =
  | "grids"
  | "orders"
  | "trades"
  | "activity"
  | "wind"
  | "pnl-snapshot"
  | "overview-snapshot";

export interface StreamMessage {
  topic: EventTopic;
  grid_id?: string;
  session_id?: string;
  ts: number;
}

type Handler = (msg: StreamMessage) => void;
type StatusHandler = (s: StreamStatus) => void;

export type StreamStatus = "connected" | "connecting" | "closed";

const handlers = new Set<Handler>();
const statusHandlers = new Set<StatusHandler>();
let es: EventSource | null = null;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let retryDelay = 1000; // exponential-ish backoff, capped
let currentStatus: StreamStatus = "closed";

function setStatus(s: StreamStatus) {
  if (currentStatus === s) return;
  currentStatus = s;
  for (const cb of [...statusHandlers]) {
    try { cb(s); } catch { /* ignore */ }
  }
}

// Topics we register explicit listeners for — EventSource's default "message"
// handler only fires for frames without `event:` tag, so we must enumerate.
const TOPICS: EventTopic[] = [
  "grids", "orders", "trades", "activity", "wind", "pnl-snapshot", "overview-snapshot",
];

function scheduleReconnect() {
  if (reconnectTimer) return;
  setStatus("connecting");
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null;
    retryDelay = Math.min(retryDelay * 2, 15_000);
    open();
  }, retryDelay);
}

function open() {
  close(false);
  setStatus("connecting");
  try {
    es = new EventSource(`${API_BASE}/events`, { withCredentials: true });
  } catch {
    scheduleReconnect();
    return;
  }

  es.addEventListener("open", () => {
    retryDelay = 1000; // reset backoff on successful connection
    setStatus("connected");
  });

  // Any topic arrives as a named event.
  for (const topic of TOPICS) {
    es.addEventListener(topic, (e: MessageEvent) => {
      try {
        const msg = JSON.parse(e.data) as StreamMessage;
        for (const h of [...handlers]) h(msg);
      } catch {
        // Drop malformed payload; a subsequent event will correct state.
      }
    });
  }

  // Server closed or network dropped → browser fires `error`. EventSource
  // auto-reconnects on its own in most browsers, but we close + reopen to
  // ensure deterministic backoff and to avoid stuck half-open sockets.
  es.addEventListener("error", () => {
    if (es?.readyState === EventSource.CLOSED) {
      setStatus("closed");
      scheduleReconnect();
    } else {
      // readyState CONNECTING (0) — transient, browser is auto-retrying.
      setStatus("connecting");
    }
  });
}

function close(resetBackoff = true) {
  if (es) {
    try { es.close(); } catch { /* ignore */ }
    es = null;
  }
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
  if (resetBackoff) {
    retryDelay = 1000;
    setStatus("closed");
  }
}

/** Subscribe to all topic events. Returns an unsubscribe fn. Lazily opens
 *  the shared connection on first subscriber; closes when the last leaves. */
export function subscribeEvents(cb: Handler): () => void {
  handlers.add(cb);
  if (handlers.size === 1) open();
  return () => {
    handlers.delete(cb);
    if (handlers.size === 0) close();
  };
}

export function eventStreamStatus(): StreamStatus {
  return currentStatus;
}

/** Subscribe to transport status transitions. Fires immediately with the
 *  current status so consumers have a value without needing a first poll. */
export function onStatusChange(cb: StatusHandler): () => void {
  statusHandlers.add(cb);
  cb(currentStatus);
  return () => { statusHandlers.delete(cb); };
}
